import { useCallback, useEffect, useState } from "react";
import DataTable, { Column } from "../../components/commons/DataTable";
import type { User, UserSearchParams } from "@/services/webServices/user/types";
import { getUsers } from "../../services/webServices/user/get";
import { useAdminLoading } from "../../context/AdminLoadingContext";
import { useInitialPageLoad } from "../../components/layouts/admin-layout/useInitialPageLoad";

const PAGE_SIZE = 12;

export default function User() {

  const [data, setData] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(0);

  const { showLoading, hideLoading } = useAdminLoading();
  const isFirstLoad = useInitialPageLoad();

  const fetchUsers = useCallback(async () => {
    if (isFirstLoad) showLoading();
    setIsLoading(true);

    try {
      const params: UserSearchParams = {
        limit: PAGE_SIZE,
        offset: (currentPage - 1) * PAGE_SIZE,
      };

      if (searchValue) params.search = searchValue;

      const response = await getUsers({ params });

      if (Array.isArray(response)) {
        setData(response);

        if (response.length < PAGE_SIZE) {
          setTotalItems((currentPage - 1) * PAGE_SIZE + response.length);
        } else {
          setTotalItems(currentPage * PAGE_SIZE + 1);
        }
      } else {
        setData([]);
        setTotalItems(0);
      }
    } finally {
      setIsLoading(false);
      if (isFirstLoad) hideLoading();
    }
  }, [currentPage, searchValue]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const handleOnRowClick = (row: User) => {
    window.open(`https://danajou.com/${row.username}`, "_blank");
  };

  const columns: Column<User>[] = [
    {
      key: "full_name",
      header: "نام",
      align: "right",
      sortable: true,
      width: "20%",
      accessor: (row) => row.full_name,
    },
    {
      key: "username",
      header: "نام کاربری",
      align: "right",
      sortable: true,
      width: "25%",
      accessor: (row) => (
        <div className="flex items-center gap-2">
          <img
            className="h-[20px] w-[20px] rounded-full object-cover"
            src={process.env.REACT_APP_File_URL + row?.avatar || "/images/avatar-alt.jpg"}
            alt={row?.full_name || "بدون نام"}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/images/avatar-alt.jpg";
            }}
          />
          <span>{row?.full_name || "بدون نام"}</span>
        </div>
      ),
    },
    {
      key: "headline",
      header: "حوزه فعالیت",
      align: "right",
      sortable: true,
      width: "20%",
      accessor: (row) => row.headline,
    },
    {
      key: "email",
      header: "ایمیل",
      align: "right",
      sortable: true,
      width: "20%",
      accessor: (row) => row.email,
    },
    {
      key: "phone_number",
      header: "شماره تماس",
      align: "right",
      sortable: true,
      width: "15%",
      accessor: (row) => row.phone_number,
    },
  ];

  return (
    <div>
      <DataTable
        title="لیست کاربران"
        data={data}
        columns={columns}
        loading={isLoading}
        loadingText="در حال بارگذاری کاربران..."
        emptyText="کاربری یافت نشد"

        searchable={true}
        searchPlaceholder="نام"
        searchKeys={["full_name"]}
        searchValue={searchValue}
        onSearchChange={handleSearchChange}

        pagination={true}
        serverSidePagination={true}
        currentPage={currentPage}
        onPageChange={handlePageChange}
        totalItems={totalItems}
        pageSize={PAGE_SIZE}

        onRowClick={handleOnRowClick}
      />
    </div>
  );
};